import { Component, inject, output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { ToastrService } from 'ngx-toastr';
import { CancellationSendMethod } from 'src/app/_enums/cancellation/cancellation-send-method.enum';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import {
  nonBlank,
  richTextRequired,
} from 'src/app/Dashboard/customer/communications/compose/compose-validators';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { EDITOR_PLUGINS, EDITOR_TOOLBAR } from 'src/app/shared/editor-config';
import { IconsModule } from 'src/app/shared/icons/icons.module';

/**
 * « Envoyer la lettre de résiliation »: the last step of a cancellation, once
 * the customer signed. The operator either mails the signed letter to the
 * insurer from here — the prose is rendered server-side and edited in place,
 * as for a centralisation — or records that they got it there themselves.
 * Both move the cancellation to SENT and hand the refreshed one up as `changed`.
 */
@Component({
  selector: 'app-cancellation-send-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ActionModalComponent,
    EditorComponent,
    IconsModule,
  ],
  templateUrl: './cancellation-send-modal.component.html',
  styleUrl: './cancellation-send-modal.component.css',
})
export class CancellationSendModalComponent {
  private readonly fb = inject(FormBuilder);

  private readonly cancellationService = inject(CancellationService);

  private readonly toastr = inject(ToastrService);

  /** A route landed: the refreshed cancellation. */
  readonly changed = output<CancellationFlowDetailsResponse>();

  @ViewChild('actionModal')
  private actionModal?: ActionModalComponent;

  protected cancellation: CancellationFlowDetailsResponse | null = null;

  protected readonly form = this.fb.group({
    mode: new FormControl<'EMAIL' | 'MARK'>('MARK', { nonNullable: true }),
    subject: new FormControl('', { nonNullable: true, validators: nonBlank }),
    body: new FormControl('', {
      nonNullable: true,
      validators: richTextRequired,
    }),
  });

  /** TinyMCE lifecycle gate; see `ComposeModalBase.modalOpen`. */
  protected modalOpen = false;

  protected renderLoading = false;

  /** Fetched once per opening, the first time the platform option is chosen; edits then stay. */
  private rendered = false;

  protected submitted = false;

  protected readonly editorPlugins = EDITOR_PLUGINS;

  protected readonly editorToolbar = EDITOR_TOOLBAR;

  protected readonly loadingText = $localize`Chargement…`;

  constructor() {
    this.form.controls.mode.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((mode) => mode === 'EMAIL' && this.fetchRender());
  }

  /** Opens on a cancellation; the option offered first follows how it was to be sent. */
  openModal(cancellation: CancellationFlowDetailsResponse): void {
    this.cancellation = cancellation;
    this.submitted = false;
    this.rendered = false;
    this.modalOpen = true;
    this.form.reset({
      mode:
        cancellation.sendMethod === CancellationSendMethod.EMAIL &&
        this.insurerEmail
          ? 'EMAIL'
          : 'MARK',
    });
    this.actionModal?.openModal();
  }

  /** Whatever dismissed the modal, the editor must not outlive it. */
  protected onClosed(): void {
    this.modalOpen = false;
    this.renderLoading = false;
  }

  protected get insurerEmail(): string {
    return this.cancellation?.insurer?.cancellationEmail?.trim() ?? '';
  }

  protected get isPlatformSend(): boolean {
    return this.form.controls.mode.value === 'EMAIL';
  }

  protected get submitDisabled(): boolean {
    return this.isPlatformSend && (this.renderLoading || this.form.invalid);
  }

  protected confirm(): void {
    const id = this.cancellation?.id;
    if (!id || this.submitted || this.submitDisabled) return;
    this.submitted = true;
    const byEmail = this.isPlatformSend;
    const request = byEmail
      ? this.cancellationService.sendToInsurer(id, {
          subject: this.form.controls.subject.value.trim(),
          body: this.form.controls.body.value,
        })
      : this.cancellationService.markSentToInsurer(id);
    request.subscribe({
      next: (res) => {
        this.submitted = false;
        this.toastr.success(
          byEmail
            ? $localize`Lettre de résiliation envoyée à l'assureur`
            : $localize`Résiliation marquée comme envoyée à l'assureur`
        );
        this.changed.emit(res.data);
        this.actionModal?.closeModal();
      },
      error: (err) => {
        this.submitted = false;
        this.toastr.error(
          getApiErrorMessage(
            err,
            $localize`Une erreur est survenue. Veuillez réessayer`
          )
        );
      },
    });
  }

  private fetchRender(): void {
    const id = this.cancellation?.id;
    if (!id || this.rendered) return;
    this.rendered = true;
    this.renderLoading = true;
    this.cancellationService.renderInsurerEmail(id).subscribe({
      next: (res) => {
        // A slow answer must not repopulate the next opening.
        if (!this.modalOpen) return;
        this.renderLoading = false;
        this.form.controls.subject.setValue(res.data.subject);
        this.form.controls.body.setValue(res.data.body);
      },
      error: (err) => {
        if (!this.modalOpen) return;
        this.renderLoading = false;
        this.rendered = false;
        this.toastr.error(
          getApiErrorMessage(err, $localize`L'e-mail n'a pas pu être préparé`)
        );
      },
    });
  }
}
